<script setup lang="ts">
import { computed, ref } from 'vue'
import { useRoute } from 'vue-router'
import type { Timestamp } from 'firebase/firestore'
import type { Personnel } from '@/types'
import { usePersonnelStore } from '@/stores/personnel'
import { useAuthStore } from '@/stores/auth'
import { useOpsStore } from '@/stores/ops'
import { usePermissionsStore } from '@/stores/permissions'
import { useCurrentOpRole } from '@/composables/useCurrentOpRole'
import FosDialog from '@/components/ui/FosDialog.vue'
import FosAlertDialog from '@/components/ui/FosAlertDialog.vue'
import FosButton from '@/components/ui/FosButton.vue'
import PermissionGuard from '@/components/ui/PermissionGuard.vue'

const props = defineProps<{ person: Personnel | null }>()
const visible = defineModel<boolean>('visible', { default: false })

const personnelStore = usePersonnelStore()
const authStore = useAuthStore()
const opsStore = useOpsStore()
const permissionsStore = usePermissionsStore()
const { opRole } = useCurrentOpRole()
const route = useRoute()
const incidentId = route.params.id as string

const showCheckOutConfirm = ref(false)

const canCheckOut = computed(() => {
  if (!props.person || props.person.time_out !== null) return false
  const isOwn = props.person.user_id === authStore.currentUser?.uid
  const canCheckOutOthers = permissionsStore.can('personnel.checkout_others', opRole.value)
  return isOwn || canCheckOutOthers
})

async function confirmCheckOut() {
  if (!props.person) return
  await personnelStore.checkOut(incidentId, opsStore.selectedOpId!, props.person.personnel_id)
  showCheckOutConfirm.value = false
  visible.value = false
}

function formatTime(ts: Timestamp | null): string {
  if (!ts) return '—'
  return ts.toDate().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
}

function formatDate(ts: Timestamp | null): string {
  if (!ts) return '—'
  return ts.toDate().toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' })
}

const quals = computed(() => {
  const q = props.person?.qualifications ?? {}
  return {
    medical: (q.medical_qual as string) || null,
    searcher: (q.searcher_type as string) || null,
    rope: (q.rope_qual as string) || null,
    misc: (q.misc_quals as string[]) ?? [],
  }
})

const statusClass = computed(() => {
  switch (props.person?.status) {
    case 'available':      return 'bg-green-50 text-green-700 dark:bg-green-950 dark:text-green-400'
    case 'assigned':       return 'bg-primary-container/20 text-primary'
    case 'out-of-service': return 'bg-surface-container-highest text-on-surface-variant'
    default:               return 'bg-surface-container-highest text-on-surface-variant'
  }
})

const statusLabel = computed(() => {
  switch (props.person?.status) {
    case 'available':      return 'Available'
    case 'assigned':       return 'Assigned'
    case 'out-of-service': return 'Out of Service'
    default:               return props.person?.status ?? ''
  }
})
</script>

<template>
  <FosDialog
    :open="visible"
    max-width="max-w-lg"
    @update:open="visible = $event"
  >
    <FosAlertDialog
      :open="showCheckOutConfirm"
      title="Confirm Check Out"
      :description="person ? `Check out ${person.first} ${person.last}?` : ''"
      confirm-label="Check Out"
      cancel-label="Cancel"
      @update:open="(v) => { if (!v) showCheckOutConfirm = false }"
      @confirm="confirmCheckOut"
    />

    <template #title>
      <div class="flex items-center gap-3">
        <span class="font-headline font-black text-lg uppercase tracking-tight text-on-surface">
          {{ person ? `${person.first} ${person.last}` : '' }}
        </span>
        <span v-if="person" :class="['inline-flex items-center px-2.5 py-0.5 text-xs font-mono font-medium', statusClass]">
          {{ statusLabel }}
        </span>
      </div>
    </template>

    <div v-if="person" class="flex flex-col gap-6 pt-1">

      <!-- Details -->
      <div>
        <p class="text-[10px] text-outline font-label uppercase tracking-widest mb-3">Details</p>
        <div class="bg-surface-container-low p-3 grid grid-cols-2 gap-x-6 gap-y-3">
          <div>
            <p class="text-[10px] text-outline font-label uppercase tracking-widest mb-0.5">Agency</p>
            <p class="text-on-surface font-mono text-sm">{{ person.agency || '—' }}</p>
          </div>
          <div>
            <p class="text-[10px] text-outline font-label uppercase tracking-widest mb-0.5">Phone</p>
            <p class="text-on-surface font-mono text-sm">{{ person.phone || '—' }}</p>
          </div>
          <div>
            <p class="text-[10px] text-outline font-label uppercase tracking-widest mb-0.5">Time In</p>
            <p class="text-on-surface font-mono text-sm">{{ formatDate(person.time_in) }} {{ formatTime(person.time_in) }}</p>
          </div>
          <div>
            <p class="text-[10px] text-outline font-label uppercase tracking-widest mb-0.5">Time Out</p>
            <p class="text-on-surface font-mono text-sm">
              {{ person.time_out ? `${formatDate(person.time_out)} ${formatTime(person.time_out)}` : '—' }}
            </p>
          </div>
        </div>
      </div>

      <!-- Qualifications -->
      <div>
        <p class="text-[10px] text-outline font-label uppercase tracking-widest mb-3">Qualifications</p>
        <div class="bg-surface-container-low p-3 grid grid-cols-2 gap-x-6 gap-y-3">
          <div>
            <p class="text-[10px] text-outline font-mono mb-0.5">Medical</p>
            <p class="text-on-surface-variant font-mono text-sm">{{ quals.medical || '—' }}</p>
          </div>
          <div>
            <p class="text-[10px] text-outline font-mono mb-0.5">Searcher Type</p>
            <p class="text-on-surface-variant font-mono text-sm">{{ quals.searcher || '—' }}</p>
          </div>
          <div>
            <p class="text-[10px] text-outline font-mono mb-0.5">Rope</p>
            <p class="text-on-surface-variant font-mono text-sm">{{ quals.rope || '—' }}</p>
          </div>
          <div v-if="quals.misc.length">
            <p class="text-[10px] text-outline font-mono mb-0.5">Other</p>
            <p class="text-on-surface-variant font-mono text-sm">{{ quals.misc.join(', ') }}</p>
          </div>
        </div>
      </div>

      <!-- Notes -->
      <div v-if="person.notes">
        <p class="text-[10px] text-outline font-label uppercase tracking-widest mb-3">Notes</p>
        <div class="bg-surface-container-low p-3">
          <p class="font-mono text-sm text-on-surface-variant">{{ person.notes }}</p>
        </div>
      </div>

    </div>

    <template v-if="person && person.time_out === null" #footer>
      <!-- Own record: no permission needed -->
      <FosButton
        v-if="person.user_id === authStore.currentUser?.uid"
        variant="secondary"
        @click="showCheckOutConfirm = true"
      >
        Check Out
      </FosButton>
      <!-- Others' records: requires checkout_others permission -->
      <PermissionGuard v-else action="personnel.checkout_others" :role="opRole">
        <FosButton variant="secondary" @click="showCheckOutConfirm = true">Check Out</FosButton>
      </PermissionGuard>
    </template>
  </FosDialog>
</template>
